import { createSignal } from 'solid-js';
import { ImagePosition, PointMarkup } from '@technology/web3d-plugin-markup';
import {
  type MeasurementsCapabilityProvider,
  type PointMeasurementType,
  type PointSymbol,
  applyPointLabel,
} from '@trimble/web3d-providers';
import { createBaseMeasurementController } from '@trimble/measurements-shared';
import type { PointMeasurementController } from './point-measurement.types';

export function createPointMeasurementController(
  provider: MeasurementsCapabilityProvider
): PointMeasurementController {
  const [showXYCoordinates, setShowXYCoordinatesSignal] = createSignal(false);
  const [showZCoordinate, setShowZCoordinateSignal] = createSignal(false);
  const [selectedPointSymbol, setSelectedPointSymbolSignal] = createSignal<
    PointSymbol | undefined
  >();

  const base = createBaseMeasurementController<PointMeasurementType>(provider, {
    applyMarkupProperties: (markup, viewerWrapper, labelValue) => {
      const showXY = showXYCoordinates();
      const showZ = showZCoordinate();
      const pointSymbol = selectedPointSymbol();
      applyPointLabel(
        markup as PointMarkup,
        viewerWrapper.viewer,
        labelValue,
        showXY,
        showZ
      );
      (markup as PointMarkup).imageUrl = pointSymbol?.url || '';
      (markup as PointMarkup).imagePosition =
        pointSymbol?.position || ImagePosition.Center;
    },
    getExtraUpdateFields: () => ({
      showXYCoordinates: showXYCoordinates(),
      showZCoordinate: showZCoordinate(),
      pointSymbol: selectedPointSymbol(),
    }),
    onInitialize: (measurement) => {
      setShowXYCoordinatesSignal(measurement.showXYCoordinates ?? false);
      setShowZCoordinateSignal(measurement.showZCoordinate ?? false);
      setSelectedPointSymbolSignal(measurement.pointSymbol);
    },
  });

  const setShowXYCoordinates = (value: boolean): void => {
    setShowXYCoordinatesSignal(value);
    base.update();
  };

  const setShowZCoordinate = (value: boolean): void => {
    setShowZCoordinateSignal(value);
    base.update();
  };

  const setSelectedPointSymbol = (value: PointSymbol | undefined): void => {
    setSelectedPointSymbolSignal(value);
    base.update();
  };

  return {
    type: 'point',
    ...base,
    showXYCoordinates,
    showZCoordinate,
    selectedPointSymbol,
    setShowXYCoordinates,
    setShowZCoordinate,
    setSelectedPointSymbol,
  };
}
